import BaseAPIClient from './base/BaseAPIClient';
import ListResponseModel from '../models/ListResponseModel';
import FleetFuelLogModel from '../models/FleetFuelLogModel';
import InspectionTemplateModel from '../models/InspectionTemplateModel';
import InspectionModel from '../models/InspectionModel';
import VehicleModel from '../models/VehicleModel';
import CompanyEquipmentModel from '../models/CompanyEquipmentModel';
import FleetDashboardSummaryModel from '../models/FleetDashboardSummaryModel';
import DriverProfileModel from '../models/DriverProfileModel';
import { FleetVehicleUiSettingsModel, FleetDriverProfileUiSettingsModel, FleetInspectionUiSettingsModel } from '../models/UiSettingsModels';

export default class FleetClient extends BaseAPIClient {
  async getDashboardSummary() {
    const data = await this._get('company/fleet/dashboard/');
    return new FleetDashboardSummaryModel(data);
  }

  async getUpcomingTrips() {
    return this._get('company/fleet/trips/upcoming/');
  }

  async getPastTrips() {
    return this._get('company/fleet/trips/past/');
  }

  async getTrip(id) {
    return this._get(`company/fleet/trips/${id}/`);
  }

  async getDriverProfiles({
    search = '',
    ordering = 'user__first_name',
    offset = 0,
    limit = 50,
    status__in,
    license_type__in,
    license_state__in,
    assigned_vehicle,
    certification_type,
    license_expiry,
    cert_expiry,
  } = {}) {
    const params = { search, ordering, offset, limit };
    if (status__in) params.status__in = status__in;
    if (license_type__in) params.license_type__in = license_type__in;
    if (license_state__in) params.license_state__in = license_state__in;
    if (assigned_vehicle) params.assigned_vehicle = assigned_vehicle;
    if (certification_type) params.certification_type = certification_type;
    if (license_expiry) params.license_expiry = license_expiry;
    if (cert_expiry) params.cert_expiry = cert_expiry;
    const data = await this._get('company/fleet/driver_profiles/', params);
    return new ListResponseModel(data, DriverProfileModel);
  }

  async getDriverProfile(id) {
    const data = await this._get(`company/fleet/driver_profiles/${id}/`);
    return new DriverProfileModel(data);
  }

  async getDeletedDriverProfileForUser(userId) {
    const data = await this._get(`company/fleet/driver_profiles/deleted_for_user/${userId}/`);
    return new DriverProfileModel(data);
  }

  async createDriverProfile(data) {
    return this._post('company/fleet/driver_profiles/', data);
  }

  async updateDriverProfile(id, data) {
    return this._patch(`company/fleet/driver_profiles/${id}/`, data);
  }

  async deleteDriverProfile(id) {
    return this._delete(`company/fleet/driver_profiles/${id}/`);
  }

  async getDriverProfilesSelectUi(params) {
    return this._get('company/fleet/driver_profiles/select_ui/', params);
  }

  async getInspectorsSelectUi(params) {
    return this._get('company/fleet/inspectors/select_ui/', params);
  }

  async createTripIssue(data) {
    return this._post('company/fleet/trip_issues/', data);
  }

  // Trip management (fleet manager)
  getTrips({
    search = '',
    ordering = '-start_datetime',
    offset = 0,
    limit = 25,
    vehicle = null,
    start_datetime__gte = null,
    start_datetime__lte = null,
    trip_status__in = null,
    archived = null,
  } = {}) {
    const params = { search, ordering, offset, limit };
    if (vehicle) params.trip_assignments__vehicle = vehicle;
    if (start_datetime__gte) params.start_datetime__gte = start_datetime__gte;
    if (start_datetime__lte) params.start_datetime__lte = start_datetime__lte;
    if (trip_status__in) params.trip_status__in = trip_status__in;
    // Use !== null instead of truthy check because archived=false is a meaningful value
    if (archived !== null) params.archived = archived;
    return this._get('company/fleet/trips/manage/', params);
  }

  archiveTrip(id, archived) {
    return this._patch(`company/fleet/trips/${id}/archive/`, { archived });
  }

  getTripManage(id) {
    return this._get(`company/fleet/trips/manage/${id}/`);
  }

  createTrip(data) {
    return this._post('company/fleet/trips/manage/', data);
  }

  updateTrip(id, data) {
    return this._patch(`company/fleet/trips/manage/${id}/`, data);
  }

  getFuelLogs({
    search = '',
    ordering = '-date',
    offset = 0,
    limit = 25,
    vehicle = null,
    logged_by = null,
    date__gte = null,
    date__lte = null,
  } = {}) {
    const params = {
      search,
      ordering,
      offset,
      limit,
    };

    if (vehicle) {
      params.vehicle = vehicle;
    }

    if (logged_by) {
      params.logged_by = logged_by;
    }

    if (date__gte) {
      params.date__gte = date__gte;
    }

    if (date__lte) {
      params.date__lte = date__lte;
    }

    return this._get('company/fleet/fuel_logs/', params).then(r => new ListResponseModel(r, FleetFuelLogModel));
  }

  getFuelLogStats({ search, vehicle, logged_by, date__gte, date__lte } = {}) {
    const params = {};
    if (search) params.search = search;
    if (vehicle) params.vehicle = vehicle;
    if (logged_by) params.logged_by = logged_by;
    if (date__gte) params.date__gte = date__gte;
    if (date__lte) params.date__lte = date__lte;
    return this._get('company/fleet/fuel_logs/stats/', params);
  }

  exportFuelLogsCsv(params = {}) {
    return this._get('company/fleet/fuel_logs_csv/', params);
  }

  createFuelLog(data) {
    return this._post('company/fleet/fuel_logs/', data);
  }

  updateFuelLog(id, data) {
    return this._patch(`company/fleet/fuel_logs/${id}/`, data);
  }

  deleteFuelLog(id) {
    return this._delete(`company/fleet/fuel_logs/${id}/`);
  }

  async getVehicleManufacturers() {
    return this._get('company/fleet/vehicles/manufacturers/');
  }

  async getVehicleTypes() {
    return this._get('company/fleet/vehicle_types/');
  }

  exportVehiclesCsv(params = {}) {
    return this._get('company/fleet/vehicles/csv_export/', params);
  }

  async getVehicles({
    search = '',
    ordering = 'name',
    offset = 0,
    limit = 200,
    location,
    statuses,
    year,
    manufacturer,
    manufacturers,
    assignedDriver,
    assignedDriverIsNull,
    reservable,
    reservableIsNull,
    reservableField = 'reservable',
    lastInspectionResult,
    registrationExpiry,
    returnAll,
  } = {}) {
    const params = {
      search,
      ordering,
      offset,
      limit,
    };

    if (location) {
      params.location = location;
    }
    if (statuses && statuses.length) {
      params.status__in = statuses.join(',');
    }
    if (year) {
      params.year = year;
    }
    if (manufacturers && manufacturers.length) {
      params.make__in = manufacturers.join(',');
    } else if (manufacturer) {
      params.make__icontains = manufacturer;
    }
    if (assignedDriver) {
      params.assigned_driver = assignedDriver;
    }
    if (assignedDriverIsNull != null) {
      params.assigned_driver__isnull = assignedDriverIsNull;
    }
    const reservableParam = reservableField || 'reservable';
    const reservableIsNullParam = `${reservableParam}__isnull`;

    if (reservable != null) {
      params[reservableParam] = reservable;
    }
    if (reservableIsNull != null) {
      params[reservableIsNullParam] = reservableIsNull;
    }
    if (lastInspectionResult) {
      params.last_inspection_result = lastInspectionResult;
    }
    if (registrationExpiry) {
      params.registration_expiry = registrationExpiry;
    }
    if (returnAll) {
      params.return_all = true;
    }

    const data = await this._get('company/fleet/vehicles/', params);
    return new ListResponseModel(data, VehicleModel);
  }

  // Inspection Templates
  async getInspectionTemplates(params) {
    const data = await this._get('company/fleet/inspection_templates/', params);
    return new ListResponseModel(data, InspectionTemplateModel);
  }

  async getInspectionTemplate(id) {
    const data = await this._get(`company/fleet/inspection_templates/${id}/`);
    return new InspectionTemplateModel(data);
  }

  async createInspectionTemplate(payload) {
    const data = await this._post('company/fleet/inspection_templates/', payload);
    return new InspectionTemplateModel(data);
  }

  async updateInspectionTemplate(id, payload) {
    const data = await this._patch(`company/fleet/inspection_templates/${id}/`, payload);
    return new InspectionTemplateModel(data);
  }

  // Inspections
  async getInspections(params) {
    const data = await this._get('company/fleet/inspections/', params);
    return new ListResponseModel(data, InspectionModel);
  }

  async getInspection(id) {
    const data = await this._get(`company/fleet/inspections/${id}/`);
    return new InspectionModel(data);
  }

  async createInspection(payload) {
    const data = await this._post('company/fleet/inspections/', payload);
    return new InspectionModel(data);
  }

  async updateInspection(id, payload) {
    const data = await this._patch(`company/fleet/inspections/${id}/`, payload);
    return new InspectionModel(data);
  }

  // Dedicated action rather than a status PATCH: the detail endpoint rejects status=canceled
  // outright, so the pending-only rule and the row lock live on this route alone.
  async cancelInspection(id) {
    const data = await this._patch(`company/fleet/inspections/${id}/cancel/`, {});
    return new InspectionModel(data);
  }

  async getVehicle(id) {
    const data = await this._get(`company/fleet/vehicles/${id}/`);
    return new VehicleModel(data);
  }

  // Returns the on-branch vehicle endpoint's full equipment+vehicle payload as a
  // CompanyEquipmentModel — used by EquipmentModal so it can render the unified
  // modal without re-fetching from the equipment endpoint (which 404s for
  // vehicles when both fleet flags are on).
  async getVehicleAsEquipment(id) {
    const data = await this._get(`company/fleet/vehicles/${id}/`);
    return new CompanyEquipmentModel(data);
  }

  async createVehicle(data) {
    return this._post('company/fleet/vehicles/', data);
  }

  async updateVehicle(id, data) {
    return this._patch(`company/fleet/vehicles/${id}/`, data);
  }

  async deleteVehicle(id) {
    return this._delete(`company/fleet/vehicles/${id}/`);
  }

  async setVehicleImage(vehicleId, fileUploadId) {
    return this._patch(`company/fleet/vehicles/${vehicleId}/`, { image: fileUploadId });
  }

  // Trip Requests
  async createTripRequest(data) {
    return this._post('company/fleet/trips/', data);
  }

  // Trip review, cancel, comments, logs — all operate on Trip via /trips/manage/
  async reviewTrip(id, data) {
    return this._post(`company/fleet/trips/manage/${id}/review/`, data);
  }

  async cancelTrip(id, data) {
    return this._post(`company/fleet/trips/manage/${id}/cancel/`, data);
  }

  async addTripComment(id, data) {
    return this._post(`company/fleet/trips/manage/${id}/comments/`, data);
  }

  async getTripLogs(id, params) {
    return this._get(`company/fleet/trips/manage/${id}/logs/`, params);
  }

  async markTripsSeen() {
    return this._post('company/fleet/trips/manage/ui/seen/');
  }

  async getTripsUnseenCount() {
    return this._get('company/fleet/trips/manage/ui/unseen_count/');
  }

  async getVehicleUiSettings() {
    const data = await this._get('company/fleet/vehicles/ui_settings/');
    return new FleetVehicleUiSettingsModel(data);
  }

  async updateVehicleUiSettings(data) {
    return this._patch('company/fleet/vehicles/ui_settings/', data);
  }

  async getDriverProfileUiSettings() {
    const data = await this._get('company/fleet/driver_profiles/ui_settings/');
    return new FleetDriverProfileUiSettingsModel(data);
  }

  async updateDriverProfileUiSettings(data) {
    return this._patch('company/fleet/driver_profiles/ui_settings/', data);
  }

  async getInspectionUiSettings() {
    const data = await this._get('company/fleet/inspections/ui_settings/');
    return new FleetInspectionUiSettingsModel(data);
  }

  async updateInspectionUiSettings(data) {
    return this._patch('company/fleet/inspections/ui_settings/', data);
  }
}
